import React from "react";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * RouteErrorBoundary — catches render-time exceptions so a single crashing page can never
 * white-screen the whole shell. Logs to console + fires an optional (fire-and-forget) beacon
 * to the backend error-tracking endpoint. The "Reload this screen" action resets the boundary
 * WITHOUT a full page reload, so tenant/auth session (held in AuthContext + localStorage,
 * above this boundary) is preserved.
 *
 * Props:
 *  - name:      human label for the guarded area (e.g. "POS Checkout")
 *  - onReset:   optional callback fired on recovery
 *  - fallback:  optional ({ error, reset }) => ReactNode custom fallback
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const scope = this.props.name || "app";
    // eslint-disable-next-line no-console
    console.error(`[RouteErrorBoundary:${scope}]`, error, info?.componentStack);
    try {
      if (BACKEND && typeof navigator !== "undefined" && navigator.sendBeacon) {
        const payload = JSON.stringify({
          scope,
          message: String(error?.message || error),
          stack: (info?.componentStack || "").slice(0, 4000),
          url: typeof window !== "undefined" ? window.location.href : "",
          ts: new Date().toISOString(),
        });
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(`${BACKEND}/api/client-errors`, blob);
      }
    } catch {
      /* beacon failures must never surface */
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error: this.state.error, reset: this.reset });

    const scope = this.props.name || "This screen";
    return (
      <div
        data-testid="route-error-boundary-fallback"
        className="flex min-h-[240px] w-full items-center justify-center p-6"
      >
        <div className="w-full max-w-md rounded-xl border border-amber-200 bg-amber-50/70 p-6 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-amber-900">{scope} hit an unexpected error</h2>
          <p className="mt-1 text-sm text-amber-800/80">
            You're still signed in and your session is safe. You can reload just this screen without
            losing your place.
          </p>
          <p className="mt-2 break-words text-xs font-mono text-amber-700/70" data-testid="route-error-message">
            {String(this.state.error?.message || this.state.error || "Unknown error")}
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.reset}
              data-testid="route-error-reload-screen"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              <RotateCcw className="h-4 w-4" /> Reload this screen
            </button>
            <button
              type="button"
              onClick={() => { if (typeof window !== "undefined") window.location.reload(); }}
              data-testid="route-error-reload-page"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
            >
              <RefreshCw className="h-4 w-4" /> Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
